import React from 'react';
import { Paper, Typography, Box, CircularProgress, Alert, Button, Chip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MessageFormatter from '../MessageFormatter';
import logo from '../../assets/images/Supervoima-Logo-White.svg';

export default function AIInsightsSection({
  analysis,
  analysisLoading,
  analysisError,
  onGenerateAnalysis,
  generating,
  privacyNotice,
  scope = 'organization',
  companyName = '',
  userName = ''
}) {
  // Format timestamp to DD.MM.YYYY HH:MM format (Finnish standard with time)
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  const getHeaderText = () => {
    if (scope === 'personal') {
      return 'AI Assessment Summary';
    }
    const name = companyName || 'Organization';
    return `AI Assessment One-Pager - ${name}`;
  };

  return (
    <Paper elevation={4} sx={{ p: 0, mb: 6, borderRadius: 3, overflow: 'hidden' }}>
      {/* Blue Header Bar with Logo, Title, and Regenerate Button */}
      <Box sx={{
        backgroundColor: 'rgb(1, 28, 45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        py: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <img
            src={logo}
            alt="Supervoima Logo"
            style={{ height: '36px', width: 'auto' }}
          />
          <Typography variant="h5" sx={{
            color: 'white',
            fontFamily: 'Bebas Neue',
            fontWeight: 'normal',
            letterSpacing: 1
          }}>
            {getHeaderText()}
          </Typography>
        </Box>
        {/* Generate/Regenerate button on blue bar - always visible when callback provided */}
        {onGenerateAnalysis && (
          <Button
            onClick={onGenerateAnalysis}
            disabled={generating}
            variant="outlined"
            size="small"
            startIcon={generating ? <CircularProgress size={16} sx={{ color: 'white' }} /> : (analysis?.executive_summary ? <RefreshIcon /> : null)}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.6)',
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255,255,255,0.1)'
              },
              '&:disabled': {
                borderColor: 'rgba(255,255,255,0.3)',
                color: 'rgba(255,255,255,0.5)'
              }
            }}
          >
            {generating ? 'Generating...' : (analysis?.executive_summary ? 'Regenerate' : 'Generate Report')}
          </Button>
        )}
      </Box>

      {/* Content Area */}
      <Box sx={{ p: 4 }}>
        {/* Title and Metadata */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{
            color: 'rgb(1, 28, 45)',
            fontFamily: 'Bebas Neue',
            fontWeight: 'normal',
            mb: 2
          }}>
            ASSESSMENT SUMMARY
          </Typography>

          {/* Metadata chips (timestamp, responses, sessions) - matches org report styling */}
          {analysis?.executive_summary && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {analysis?.analysis_generated_at && (
                <Chip
                  label={`Generated: ${formatTimestamp(analysis.analysis_generated_at)}`}
                  size="small"
                  variant="outlined"
                />
              )}
              {analysis?.response_count != null && (
                <Chip
                  label={`${analysis.response_count} Responses`}
                  size="small"
                  variant="outlined"
                />
              )}
              {analysis?.unique_user_count != null && (
                <Chip
                  label={`${analysis.unique_user_count} Users`}
                  size="small"
                  variant="outlined"
                />
              )}
              {analysis?.session_count != null && (
                <Chip
                  label={`${analysis.session_count} ${analysis.session_count === 1 ? 'Session' : 'Sessions'}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          )}

          {!analysis?.executive_summary && !analysisLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Generate an executive summary report. This can take 1-2 minutes depending on data amount.
            </Typography>
          )}
        </Box>

        {analysisLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
            <CircularProgress sx={{ color: '#e04111' }} />
            <Typography variant="body1" sx={{ ml: 2, color: 'text.secondary' }}>
              Analyzing responses for personalized insights...
            </Typography>
          </Box>
        )}

        {analysisError && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Unable to generate AI analysis: {analysisError}
            {onGenerateAnalysis && (
              <Button
                onClick={onGenerateAnalysis}
                size="small"
                sx={{
                  ml: 2,
                  borderRadius: '20px',
                  textTransform: 'none'
                }}
              >
                Retry
              </Button>
            )}
          </Alert>
        )}

        {analysis && (
          <Box>
            {/* AI-Generated Analysis Content - Rendered as Markdown */}
            {analysis.executive_summary && (
              <Box sx={{
                mt: 2,
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  fontFamily: 'Bebas Neue',
                  color: 'rgb(1, 28, 45)',
                  fontWeight: 'normal',
                  fontSize: {
                    h1: '2.5rem',
                    h2: '2.0rem',
                    h3: '1.7rem',
                    h4: '1.4rem',
                    h5: '1.2rem',
                    h6: '1.1rem'
                  }
                },
                '& p': {
                  lineHeight: 1.9,
                  marginBottom: '1rem'
                },
                '& ul, & ol': {
                  marginBottom: '1.5rem',
                  marginTop: '1rem'
                },
                '& li': {
                  marginBottom: '0.7rem',
                  lineHeight: 1.7
                }
              }}>
                <MessageFormatter content={analysis.executive_summary} />
              </Box>
            )}
          </Box>
        )}

        {/* Generate Analysis Button - Only shown when no analysis exists */}
        {!analysisLoading && !analysisError && !analysis?.executive_summary && !onGenerateAnalysis && (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="body1" color="text.secondary">
              AI analysis will be generated automatically.
            </Typography>
          </Box>
        )}

        {/* Privacy Notice and Disclaimer at Bottom - Only show when report is visible */}
        {analysis?.executive_summary && (
          <Box sx={{ mt: 4 }}>
            {privacyNotice && (
              <Alert severity="info" sx={{ mb: 2, backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>
                {privacyNotice}
              </Alert>
            )}
            <Typography variant="caption" sx={{
              display: 'block',
              textAlign: 'center',
              color: 'text.secondary',
              fontStyle: 'italic',
              fontSize: '0.7rem'
            }}>
              Insights and recommendations generated by AI. Use results as guidance only.
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}
